import React, { ReactElement, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

import { Button, ButtonColor, ButtonVariant } from '@videoblocks/storywind';

interface Props {
  wistiaSrc?: string;
  darkMode?: boolean;
  imagesFirst?: boolean;
  content?: string;
  showCallToAction?: boolean;
  h1Header?: boolean;
  callToActionType?: ButtonVariant;
  callToActionHref?: string;
  callToActionText?: string;
  callToActionColor?: ButtonColor;
  videoSrc?: string;
  imageSrc?: string;
  imageSourceAlt?: string;
  heading?: string;
  showSectionIcon?: boolean;
  sectionIconSrc?: string[];
  sectionIconAlt?: string[];
  classNames?: string;
}

const ImageAndText = ({
  wistiaSrc = '',
  darkMode = false,
  imagesFirst = false,
  content = '',
  showCallToAction = false,
  h1Header = false,
  callToActionType = ButtonVariant.Contained,
  callToActionHref = '#',
  callToActionText = '',
  callToActionColor = ButtonColor.Primary,
  videoSrc = '',
  imageSrc = '',
  imageSourceAlt = '',
  heading = '',
  showSectionIcon = false,
  sectionIconSrc = [],
  sectionIconAlt = [],
  classNames = '',
}: Props): ReactElement => {
  useEffect(() => {
    if (wistiaSrc !== '') {
      const script1 = document.createElement('script');
      script1.src =
        'https://fast.wistia.com/embed/medias/' + wistiaSrc + '.jsonp';
      script1.async = true;
      document.body.appendChild(script1);

      const script2 = document.createElement('script');
      script2.src = 'https://fast.wistia.com/assets/external/E-v1.js';
      script2.async = true;
      document.body.appendChild(script2);
    }
  }, []);

  const handleLoad = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    event.currentTarget.parentElement.style.opacity = '1';
  };

  return (
    <section
      className={twMerge(
        classNames,
        'w-full',
        darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'
      )}
    >
      <div
        className="
        max-w-7xl
          px-7
          md:px-24
          mx-auto
          py-12
          md:py-16
          lg:py-18
          flex
          flex-col
          flex-nowrap
          lg:flex-row
          items-center
          gap-8
          justify-start"
      >
        <div
          className={`${imagesFirst ? 'lg:order-1 ' : 'lg:order-2 '} ${
            videoSrc !== '' || wistiaSrc !== '' ? 'xl:w-2/3' : 'lg:w-1/2'
          } w-full`}
        >
          <div className="overflow-hidden">
            {videoSrc && (
              <div className="block aspect-w-16 aspect-h-9 rounded-3xl">
                <video
                  autoPlay={false}
                  className="inset-0 object-cover rounded-2.5xl w-full h-full bg-gray-400"
                  loop={false}
                  muted={false}
                  playsInline={true}
                  poster={imageSrc}
                  preload="auto"
                  controls
                >
                  <source src={videoSrc} type="video/webm" />
                  <source src={videoSrc} type="video/mp4" />
                  <img
                    src={imageSrc}
                    alt={imageSourceAlt}
                    width={16}
                    height={9}
                  />
                </video>
              </div>
            )}
            {wistiaSrc && (
              <div>
                <div
                  className="wistia_responsive_padding"
                  style={{ padding: '56.25% 0 0 0', position: 'relative' }}
                >
                  <div
                    className="wistia_responsive_wrapper"
                    style={{
                      height: '100%',
                      left: '0',
                      position: 'absolute',
                      top: '0',
                      width: '100%',
                    }}
                  >
                    <div
                      className={`wistia_embed wistia_async_${wistiaSrc} seo=false videoFoam=true`}
                      style={{
                        height: '100%',
                        position: 'relative',
                        width: '100%',
                      }}
                    >
                      <div
                        className="wistia_swatch"
                        style={{
                          height: '100%',
                          left: '0',
                          opacity: '0',
                          overflow: 'hidden',
                          position: 'absolute',
                          top: '0',
                          transition: 'opacity 200ms',
                          width: '100%',
                        }}
                      >
                        <img
                          alt="Wistia"
                          src={`https://fast.wistia.com/embed/medias/${wistiaSrc}/swatch`}
                          style={{
                            filter: 'blur(5px)',
                            height: '100%',
                            objectFit: 'contain',
                            width: '100%',
                          }}
                          width={16}
                          height={9}
                          aria-hidden="true"
                          onLoad={handleLoad}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <img
              className={videoSrc || wistiaSrc ? 'hidden w-auto' : 'w-auto'}
              src={imageSrc}
              alt={imageSourceAlt}
              width={16}
              height={9}
            />
          </div>
        </div>
        <div
          className={`${imagesFirst ? 'lg:order-2 ' : 'lg:order-1 '} ${
            videoSrc != '' ? 'xl:w-1/3' : 'lg:w-1/2'
          } w-full`}
        >
          {showSectionIcon && (
            <div className="flex w-8 lg:w-12">
              {sectionIconSrc.map((src, index) => (
                <img
                  className="mr-2"
                  src={src}
                  alt={sectionIconAlt[index]}
                  width={48}
                  height={48}
                  key={src}
                />
              ))}
            </div>
          )}

          {h1Header ? (
            <h1 className="font-bold text-3xl lg:text-5xl md:mb-6 md:mt-6">
              {heading}
            </h1>
          ) : (
            <h2 className="font-bold text-3xl lg:text-5xl md:mb-6 md:mt-6 font-serif">
              {heading}
            </h2>
          )}
          <div
            className={'font-normal text-base md:text-xl leading-7 md:mb-6'}
            dangerouslySetInnerHTML={{ __html: content }}
          ></div>
          <div className="text-center flex justify-start">
            {showCallToAction && (
              <div className="flex justify-start text-center pt-4">
                <Button
                  className={`${
                    callToActionHref ? 'text-black no-underline' : ''
                  }`}
                  href={callToActionHref}
                  variant={callToActionType}
                  color={callToActionColor}
                >
                  {callToActionText}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImageAndText;
